import React, { useState, ReactNode } from 'react';
import {
  Box,
  IconButton
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface SplitScreenLayoutProps {
  leftPanel: ReactNode;
  rightPanel: ReactNode;
  breakpoint?: 'sm' | 'md' | 'lg' | 'xl';
  fullSizeInitialState?: 'left-panel-open' | 'right-panel-fullscreen';
  smallScreenInitialState?: 'left-panel-open' | 'right-panel-open';
}

const SplitScreenLayout: React.FC<SplitScreenLayoutProps> = ({
  leftPanel,
  rightPanel,
  breakpoint = 'lg',
  fullSizeInitialState = 'left-panel-open',
  smallScreenInitialState = 'left-panel-open'
}) => {
  const [rightPanelFullscreen, setRightPanelFullscreen] = useState<boolean>(fullSizeInitialState === 'right-panel-fullscreen');
  const [showRightPanelOnSmall, setShowRightPanelOnSmall] = useState<boolean>(smallScreenInitialState === 'right-panel-open');

  const handleToggleRightPanelFullscreen = () => {
    setRightPanelFullscreen(!rightPanelFullscreen);
  }

  const handleToggleSmallScreenPanel = () => {
    setShowRightPanelOnSmall(!showRightPanelOnSmall);
  }

  // Shared toggle button styling
  const toggleButtonBaseStyle = {
    zIndex: 1000,
    backgroundColor: 'primary.main',
    border: '1px solid',
    borderColor: 'primary.light',
    color: 'primary.contrastText',
    padding: '8px',
    '&:hover': {
      backgroundColor: 'primary.light',
    },
    '& .MuiSvgIcon-root': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  };

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: { xs: 'column', [breakpoint]: 'row' }, 
      justifyContent: { xs: 'center', [breakpoint]: 'flex-start' }
    }}>
      {/* Left Panel */}
      <Box sx={{ 
        flex: { xs: '0 0 auto', [breakpoint]: '0 0 auto' },
        display: rightPanelFullscreen ? 'none' : { xs: showRightPanelOnSmall ? 'none' : 'block', [breakpoint]: 'block' },
        position: 'relative',
        overflow: 'auto',
        height: '100%'
      }}>
        {leftPanel}
        
        {/* Toggle Button for Small Screens */}
        <IconButton
          onClick={handleToggleSmallScreenPanel}
          sx={{
            ...toggleButtonBaseStyle,
            display: { xs: 'block', [breakpoint]: 'none' },
            position: 'fixed',
            top: '50%',
            transform: 'translateY(-50%)',
            right: 0,
            borderRadius: '50% 0 0 50%',
            borderRight: 'none'
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
      </Box>

      {/* Right Panel */}
      <Box sx={{ 
        flex: { xs: showRightPanelOnSmall ? '1 1 100%' : '0 0 0%', [breakpoint]: rightPanelFullscreen ? '1 1 100%' : '1 1 auto' },
        display: { xs: showRightPanelOnSmall ? 'block' : 'none', [breakpoint]: 'block' },
        position: 'relative',
        width: { xs: showRightPanelOnSmall ? '100%' : 0, [breakpoint]: 'auto' },
        height: { xs: showRightPanelOnSmall ? '100%' : 0, [breakpoint]: '100%' }
      }}>
        {/* Toggle Button for Large Screens */}
        <IconButton
          onClick={handleToggleRightPanelFullscreen}
          sx={{
            ...toggleButtonBaseStyle,
            display: { xs: 'none', [breakpoint]: 'block' },
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            borderRadius: '0 50% 50% 0',
            left: 0
          }}
        >
          {rightPanelFullscreen ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
        
        {/* Toggle Button for Small Screens */}
        <IconButton
          onClick={handleToggleSmallScreenPanel}
          sx={{
            ...toggleButtonBaseStyle,
            display: { xs: 'block', [breakpoint]: 'none' },
            position: 'fixed',
            top: '50%',
            transform: 'translateY(-50%)',
            left: 0,
            borderRadius: '0 50% 50% 0',
            borderLeft: 'none'
          }}
        >
          <ChevronRightIcon />
        </IconButton>
        
        <Box sx={{ 
          width: '100%', 
          height: '100%',
          overflow: 'auto'
        }}>
          {rightPanel}
        </Box>
      </Box>
    </Box>
  );
};

export default SplitScreenLayout;
